import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { MenuList } from '@/components/v4';
import {
    Box,
    HStack,
    VStack,
    Progress,
    Menu,
    MenuButton,
    MenuItem,
    IconButton,
} from '@chakra-ui/react';
import { Card, CardBody } from 'components/v4/Card';
import Text from 'components/v4/Text';
import Image from 'components/v4/Image';
import CardDetails from '../../components/CardDetails';
import { useServicesDashboard } from 'context/ServicesDashboardContext';
import ServiceStatus from './ServiceStatus';
import { IconComponent } from '../../components/IconComponent';
import { BsThreeDotsVertical } from 'react-icons/bs';
import MicrosoftGraphMenuItems from '../../components/MicrosoftGraphMenuItems';
import {
    MICROSOFT_GRAPH,
    APP_AUTH,
    APP_AUTH_GCCH,
    EIGHTBYEIGHT_AUTH,
    ZOOM_AUTH,
    WEBEX_AUTH,
} from '../../constants';
import { PlatformEnums } from 'utils/platforms';
import { hasRenderableContent } from '../../components/helpers/menuContentHelper';
import {
    getSimpleRedirectAuthServiceTypeByAppAuthTypeID,
    getSimpleRedirectPlatformByServiceType,
    getSimpleRedirectPlatformByAuthServiceType,
    getPendingAccessPlatform,
} from 'services/platformRegistry';

const AppAuthCard = ({
    serviceWizard,
    categoryData,
    hasEightByEightAppAuth,
    hasZoomAppAuth,
    hasWebexAppAuth,
    msServiceAuth = {},
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const serviceStatus = useServicesDashboard();
    const getAuthServiceCardStatus = (authServiceType, authService) => {
        let statusKey = authServiceType;
        if (authServiceType === MICROSOFT_GRAPH) statusKey = APP_AUTH;
        // The GCC High Microsoft auth shares appAuthTypeID 'Microsoft'; it is
        // disambiguated by platformID and stored under APP_AUTH_GCCH.
        if (
            authServiceType === MICROSOFT_GRAPH &&
            authService?.platformID === PlatformEnums.GCC
        )
            statusKey = APP_AUTH_GCCH;
        // Simple-redirect platforms (EightByEight, RingCentral, DialPad, Zoom,
        // Webex) receive the API's appAuthTypeID here; translate it to the
        // dashboard status-context key it's stored under.
        statusKey =
            getSimpleRedirectAuthServiceTypeByAppAuthTypeID(authServiceType) ??
            statusKey;
        const mapped = serviceStatus[statusKey];
        if (mapped?.color) return mapped;
        // For AppAuth-only services the auth key may be unpopulated; fall back to own-type status
        if (statusKey !== authServiceType) {
            const own = serviceStatus[authServiceType];
            if (own?.color) return own;
        }
        // Last resort: derive color from statusDisplay on the service object itself
        const detail = authService?.statusDisplay?.statusDisplayDetail;
        if (detail) {
            return {
                color: 'yellow',
                statusLabel:
                    detail === 'PendingAccess' ? 'Pending Access' : detail,
                animation: false,
            };
        }
        return mapped || {};
    };
    const accountScope = useSelector((state) => state.login.requiredScope);

    const handleAuthGrantAccess = (authService) => {
        // Zoom/8x8/Webex/RingCentral/DialPad all just redirect to a login link —
        // see services/platformRegistry.js. Their case arms may key off either
        // the "service" or "auth" serviceType constant.
        const simplePlatform =
            getSimpleRedirectPlatformByServiceType(authService.serviceType) ??
            getSimpleRedirectPlatformByAuthServiceType(authService.serviceType);
        if (simplePlatform) {
            serviceWizard?.grantAccessForServiceType(
                simplePlatform.serviceType,
            );
            return;
        }
        switch (authService.serviceType) {
            case APP_AUTH:
            case MICROSOFT_GRAPH:
                // GCC High uses its own login link (getGCCHLoginLink) rather
                // than the commercial Microsoft one.
                if (authService.platformID === PlatformEnums.GCC) {
                    serviceWizard?.gcchAuth();
                } else {
                    serviceWizard?.microsoftAuth();
                }
                break;
        }
    };

    const cardDetails = CardDetails(
        categoryData?.authServices[0]?.serviceType,
        categoryData?.authServices[0]?.platformID,
    );

    const renderAuthServiceMenuItems = (authServices) => {
        return authServices.map((authService) => {
            // RingCentral/DialPad only ever show a "Grant Access" item, gated
            // on the auth card's own Pending Access status (no View Service
            // item, no accountScope gate — see the '_AUTH' family shape below
            // for that shape).
            const pendingAccessPlatform = getPendingAccessPlatform(
                authService.serviceType,
            );
            if (pendingAccessPlatform) {
                const cardStatus = getAuthServiceCardStatus(
                    authService.serviceType,
                    authService,
                );
                return (
                    <Fragment key={authService.serviceType}>
                        {cardStatus?.statusLabel === 'Pending Access' && (
                            <MenuItem
                                icon={
                                    <IconComponent
                                        icon="Settings"
                                        color={darkMode ? 'white' : 'black'}
                                    />
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    serviceWizard?.grantAccessForServiceType(
                                        pendingAccessPlatform.serviceType,
                                    );
                                }}
                                data-walkthroughid={`/dashboard-services/${pendingAccessPlatform.walkthroughKey}-auth/grant-access`}>
                                <Text marginBottom="0">Grant Access</Text>
                            </MenuItem>
                        )}
                    </Fragment>
                );
            }
            switch (authService.serviceType) {
                case EIGHTBYEIGHT_AUTH:
                case 'EightByEight':
                    return (
                        <Fragment key={authService.serviceType}>
                            {authService.service?.authPolicy !== 0 &&
                                !hasEightByEightAppAuth && (
                                    <MenuItem
                                        icon={
                                            <IconComponent
                                                icon="Settings"
                                                color={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            serviceWizard?.eightByEightAuth();
                                        }}
                                        data-walkthroughid="/dashboard-services/eightbyeight-auth/grant-access">
                                        <Text marginBottom="0">
                                            Grant Access
                                        </Text>
                                    </MenuItem>
                                )}
                        </Fragment>
                    );
                case ZOOM_AUTH: {
                    // FIX:(2725): Grant Access missing from Zoom App Auth —
                    // once the app auth exists, only re-show Grant Access
                    // here when the app auth itself has degraded.
                    const zoomCardStatus = getAuthServiceCardStatus(
                        authService.serviceType,
                        authService,
                    );
                    return (
                        <Fragment key={authService.serviceType}>
                            {authService.service?.authPolicy !== 0 &&
                                (!hasZoomAppAuth ||
                                    zoomCardStatus?.grantAccess) && (
                                    <MenuItem
                                        icon={
                                            <IconComponent
                                                icon="Settings"
                                                color={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            serviceWizard?.zoomAuth();
                                        }}
                                        data-walkthroughid="/dashboard-services/zoom-auth/grant-access">
                                        <Text marginBottom="0">
                                            Grant Access
                                        </Text>
                                    </MenuItem>
                                )}
                        </Fragment>
                    );
                }
                case APP_AUTH:
                case MICROSOFT_GRAPH: {
                    // 2629: the service record alone doesn't carry the
                    // commercial AppAuth's msGraphAppInstanceID/lastAccessSuccess,
                    // so merge in the AppAuth data (GCC High keeps its own
                    // service data, since it uses a separate AppAuth record).
                    const isGCCH = authService.platformID === PlatformEnums.GCC;
                    const migrationServiceObj = isGCCH
                        ? authService.service
                        : {
                              ...authService.service,
                              msGraphAppInstanceID:
                                  msServiceAuth?.msGraphAppInstanceID,
                              lastAccessSuccess:
                                  msServiceAuth?.lastAccessSuccess,
                              statusDisplay:
                                  msServiceAuth?.statusDisplay ??
                                  authService.service?.statusDisplay,
                          };
                    return (
                        <MicrosoftGraphMenuItems
                            key={authService.serviceType}
                            accountScope={accountScope}
                            serviceObj={migrationServiceObj}
                            cardStatus={
                                (authService.platformID === PlatformEnums.GCC
                                    ? serviceStatus[APP_AUTH_GCCH]
                                    : serviceStatus[APP_AUTH]) || {}
                            }
                            darkMode={darkMode}
                            handleGrantAccess={() =>
                                authService.platformID === PlatformEnums.GCC
                                    ? serviceWizard?.gcchAuth()
                                    : serviceWizard?.microsoftAuth()
                            }
                            service={authService.service}
                            data={{
                                ...authService.service,
                                hasAppAuthData: true,
                            }}
                        />
                    );
                }
                case WEBEX_AUTH:
                    return (
                        <Fragment key={authService.serviceType}>
                            {/* {accountScope >= 40 && (
                                <MenuItem
                                    icon={
                                        <ViewIcon
                                            icon="View"
                                            color={darkMode ? 'white' : 'black'}
                                        />
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const url = `${path.SERVICES_WEBEX_AUTH}/${authService.service?.id}/edit`;
                                        navigate(url, {
                                            state: {
                                                service: authService.service,
                                            },
                                        });
                                    }}>
                                    <Text marginBottom="0">View Service</Text>
                                </MenuItem>
                            )} */}

                            {authService.service?.authPolicy !== 0 &&
                                !hasWebexAppAuth && (
                                    <MenuItem
                                        icon={
                                            <IconComponent
                                                icon="Settings"
                                                color={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            serviceWizard?.webexAuth();
                                        }}
                                        data-walkthroughid="/dashboard-services/webex-auth/grant-access">
                                        <Text marginBottom="0">
                                            Grant Access
                                        </Text>
                                    </MenuItem>
                                )}
                        </Fragment>
                    );
                default:
                    return null;
            }
        });
    };

    const authMenuItems =
        categoryData?.authServices?.length > 0
            ? renderAuthServiceMenuItems(categoryData.authServices)
            : [];
    const hasVisibleMenuItems = hasRenderableContent(authMenuItems);

    return (
        <Card
            bg={darkMode ? 'dark.tcap' : null}
            sx={{
                border: '1px solid',
                borderColor: darkMode ? 'gray.700' : 'gray.200',
                boxShadow: darkMode
                    ? 'none'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            }}>
            {/* Progress Bar - Left Border */}
            {categoryData?.authServices.length > 0 &&
                (() => {
                    const authCardStatus = getAuthServiceCardStatus(
                        categoryData?.authServices[0]?.serviceType,
                        categoryData?.authServices[0],
                    );
                    return (
                        <Progress
                            value={100}
                            sx={{
                                width: '10px',
                                position: 'absolute',
                                left: 0,
                                h: '100%',
                                borderRadius: '5px 0 0 5px',
                            }}
                            colorScheme={authCardStatus?.color}
                            hasStripe={authCardStatus?.animation}
                            isAnimated={authCardStatus?.animation}
                        />
                    );
                })()}

            <CardBody
                sx={{
                    display: 'flex',
                    flexDir: 'column',
                }}>
                <VStack alignItems="flex-start" width="100%" spacing={2}>
                    <HStack alignItems="center" width="100%">
                        <HStack alignItems="center" width="100%">
                            <Image
                                className="rounded-xl"
                                src={cardDetails?.icon}
                                sx={{ maxWidth: '30px' }}
                            />
                            <Text fontWeight="bold">
                                {cardDetails?.serviceName}
                            </Text>
                            {/* Status */}
                            <Box className="mb-[15px] flex justify-between">
                                {/* ServiceStatus Component */}
                                {categoryData?.authServices.length > 0 &&
                                    (() => {
                                        const authService =
                                            categoryData?.authServices[0];
                                        const authCardStatus =
                                            getAuthServiceCardStatus(
                                                authService.serviceType,
                                                authService,
                                            );

                                        return (
                                            <ServiceStatus
                                                service={authService.service}
                                                cardStatus={authCardStatus}
                                                handleActions={{
                                                    handleGrantAccess: () =>
                                                        handleAuthGrantAccess(
                                                            authService,
                                                        ),
                                                }}
                                            />
                                        );
                                    })()}
                            </Box>
                        </HStack>
                        {/* Menu Items */}
                        <Box as="span" flex="0" zIndex={100} textAlign="right">
                            {hasVisibleMenuItems && (
                                <Menu>
                                    <MenuButton
                                        as={IconButton}
                                        icon={<BsThreeDotsVertical />}
                                        aria-label="editPlatform"
                                        variant="ghost"
                                        color={darkMode ? 'white' : 'black'}
                                        _hover={{
                                            bg: darkMode
                                                ? 'whiteAlpha.200'
                                                : 'blackAlpha.100',
                                        }}
                                        _active={{
                                            bg: darkMode
                                                ? 'whiteAlpha.300'
                                                : 'blackAlpha.200',
                                        }}
                                        _focus={{ boxShadow: 'none' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    />
                                    <MenuList>{authMenuItems}</MenuList>
                                </Menu>
                            )}
                        </Box>
                    </HStack>
                </VStack>
            </CardBody>
        </Card>
    );
};

export default AppAuthCard;
